import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  Database, 
  Table2, 
  Key, 
  Link2, 
  Star,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  Info,
  ExternalLink,
  Copy,
  Check,
  Play,
  Loader2,
  FileCode,
  Download
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

// Power Query M Script Generator - supports both Power BI and Excel
const generatePowerQueryScript = (baseUrl, apiKey, mode = 'powerbi') => {
  const isExcel = mode === 'excel';
  const appName = isExcel ? 'Excel' : 'Power BI';
  
  // Excel needs separate queries for each table, Power BI can use a record
  if (isExcel) {
    return generateExcelScript(baseUrl, apiKey);
  }
  
  return `// ============================================================
// Pulse Data Connector for Microsoft Power BI
// Generated: ${new Date().toISOString()}
// ============================================================
// 
// INSTRUCTIONS:
// 1. In Power BI Desktop, go to Home > Transform Data > Advanced Editor
// 2. Replace all content with this script
// 3. Click "Done" to load all tables
// 4. The script will create tables for each collection plus expanded relationship tables
//
// ============================================================

let
    // Configuration
    BaseUrl = "${baseUrl}",
    ApiKey = "${apiKey || 'YOUR_API_KEY_HERE'}",
    
    // Helper function to call API endpoints
    GetData = (endpoint as text) as table =>
        let
            Url = BaseUrl & endpoint & "?api_key=" & ApiKey,
            Source = Json.Document(Web.Contents(Url)),
            ToTable = if Source is list then Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error) else #table({}, {{Source}}),
            Expanded = if Table.ColumnCount(ToTable) = 1 and Table.ColumnNames(ToTable){0} = "Column1" 
                       then Table.ExpandRecordColumn(ToTable, "Column1", Record.FieldNames(ToTable{0}[Column1])) 
                       else ToTable
        in
            Expanded,
    
    // ==================== PRIMARY TABLES ====================
    
    // Users - Application users (Account Owners, Admins)
    Users = GetData("/api/admin/powerbi/users"),
    
    // Organizations - Customer organizations/accounts
    Organizations = GetData("/api/admin/powerbi/organizations"),
    
    // Projects - Projects within organizations
    Projects = GetData("/api/admin/powerbi/projects"),
    
    // Stakeholders - Project stakeholders (invited users)
    Stakeholders_Raw = GetData("/api/admin/powerbi/stakeholders"),
    
    // Expand segment_assignments array to separate table
    Stakeholders = Table.SelectColumns(Stakeholders_Raw, 
        List.Select(Table.ColumnNames(Stakeholders_Raw), each _ <> "segment_assignments"), 
        MissingField.Ignore),
    
    // Create Stakeholder_Segments junction table from segment_assignments
    Stakeholder_Segments = 
        let
            Source = Stakeholders_Raw,
            FilteredRows = Table.SelectRows(Source, each [segment_assignments] <> null and List.Count([segment_assignments]) > 0),
            ExpandedList = Table.ExpandListColumn(FilteredRows, "segment_assignments"),
            ExpandedRecords = Table.ExpandRecordColumn(ExpandedList, "segment_assignments", 
                {"segment_id", "segment_name", "segment_type"}, 
                {"segment_id", "segment_name", "segment_type"}),
            SelectedColumns = Table.SelectColumns(ExpandedRecords, {"id", "segment_id", "segment_name", "segment_type"}, MissingField.Ignore),
            RenamedColumns = Table.RenameColumns(SelectedColumns, {{"id", "stakeholder_id"}})
        in
            RenamedColumns,
    
    // ==================== SECONDARY TABLES ====================
    
    // Segment Types - Categories of segments (e.g., Department, Location)
    SegmentTypes = GetData("/api/admin/powerbi/segment-types"),
    
    // Segments - Individual segments within types
    Segments = GetData("/api/admin/powerbi/segments"),
    
    // Drivers - Sentiment drivers (categories for questions)
    Drivers = GetData("/api/admin/powerbi/drivers"),
    
    // Questions - Survey questions (About Me, About Others, Public)
    Questions_Raw = GetData("/api/admin/powerbi/questions"),
    Questions = Table.SelectColumns(Questions_Raw, 
        List.Select(Table.ColumnNames(Questions_Raw), each _ <> "options"), 
        MissingField.Ignore),
    
    // ==================== RESPONSE TABLES ====================
    
    // Survey Responses - About Me survey responses
    SurveyResponses = GetData("/api/admin/powerbi/survey-responses"),
    
    // About Others Responses - About Others survey responses
    AboutOthersResponses = GetData("/api/admin/powerbi/about-others-responses"),
    
    // About Others Selections - Who selected whom for About Others
    AboutOthersSelections_Raw = GetData("/api/admin/powerbi/about-others-selections"),
    AboutOthersSelections = Table.SelectColumns(AboutOthersSelections_Raw, 
        List.Select(Table.ColumnNames(AboutOthersSelections_Raw), each _ <> "mapping_category_ids"), 
        MissingField.Ignore),
    
    // Invitations - Stakeholder invitations
    Invitations = GetData("/api/admin/powerbi/invitations"),
    
    // ==================== SYSTEM TABLES ====================
    
    // Subscriptions - Account owner subscriptions
    Subscriptions = GetData("/api/admin/powerbi/subscriptions"),
    
    // Pricing Tiers - Pricing plan definitions
    PricingTiers_Raw = GetData("/api/admin/powerbi/pricing-tiers"),
    PricingTiers = Table.SelectColumns(PricingTiers_Raw, 
        List.Select(Table.ColumnNames(PricingTiers_Raw), each _ <> "features"), 
        MissingField.Ignore),
    
    // Create PricingTier_Features expanded table
    PricingTier_Features = 
        let
            Source = PricingTiers_Raw,
            FilteredRows = Table.SelectRows(Source, each [features] <> null and List.Count([features]) > 0),
            ExpandedList = Table.ExpandListColumn(FilteredRows, "features"),
            SelectedColumns = Table.SelectColumns(ExpandedList, {"id", "display_name", "features"}, MissingField.Ignore),
            RenamedColumns = Table.RenameColumns(SelectedColumns, {{"id", "tier_id"}, {"features", "feature"}})
        in
            RenamedColumns,
    
    // Dashboard Configs - Dashboard configuration per project
    DashboardConfigs_Raw = GetData("/api/admin/powerbi/dashboard-configs"),
    DashboardConfigs = Table.SelectColumns(DashboardConfigs_Raw, 
        List.Select(Table.ColumnNames(DashboardConfigs_Raw), 
            each _ <> "response_summary_question_ids" and _ <> "response_summary_questions"), 
        MissingField.Ignore),
    
    // ==================== OUTPUT ====================
    // Return a record containing all tables
    
    Output = [
        // Primary Tables
        Users = Users,
        Organizations = Organizations,
        Projects = Projects,
        Stakeholders = Stakeholders,
        Stakeholder_Segments = Stakeholder_Segments,
        
        // Secondary Tables
        SegmentTypes = SegmentTypes,
        Segments = Segments,
        Drivers = Drivers,
        Questions = Questions,
        
        // Response Tables
        SurveyResponses = SurveyResponses,
        AboutOthersResponses = AboutOthersResponses,
        AboutOthersSelections = AboutOthersSelections,
        Invitations = Invitations,
        
        // System Tables
        Subscriptions = Subscriptions,
        PricingTiers = PricingTiers,
        PricingTier_Features = PricingTier_Features,
        DashboardConfigs = DashboardConfigs
    ]
in
    Output

// ============================================================
// RELATIONSHIP GUIDE FOR POWER BI MODEL:
// ============================================================
// 
// PRIMARY RELATIONSHIPS:
// - Users.id → Organizations.owner_id (1:N)
// - Organizations.id → Projects.organization_id (1:N)
// - Projects.id → Stakeholders.project_id (1:N)
// - Stakeholders.id → Stakeholder_Segments.stakeholder_id (1:N)
// - Segments.id → Stakeholder_Segments.segment_id (N:1)
// 
// SECONDARY RELATIONSHIPS:
// - SegmentTypes.id → Segments.segment_type_id (1:N)
// - Projects.id → SegmentTypes.project_id (1:N)
// - Projects.id → Drivers.project_id (1:N)
// - Drivers.id → Questions.driver_id (1:N)
// - Projects.id → Questions.project_id (1:N)
// 
// RESPONSE RELATIONSHIPS:
// - Stakeholders.id → SurveyResponses.stakeholder_id (1:N)
// - Questions.id → SurveyResponses.question_id (N:1)
// - Questions.id → AboutOthersResponses.question_id (N:1)
// - AboutOthersSelections.id → AboutOthersResponses.selection_id (1:N)
// - Stakeholders.id → Invitations.stakeholder_id (1:1)
// 
// SYSTEM RELATIONSHIPS:
// - Users.id → Subscriptions.account_owner_id (1:1)
// - PricingTiers.id → Subscriptions.tier_id (1:N)
// - PricingTiers.id → PricingTier_Features.tier_id (1:N)
// - Projects.id → DashboardConfigs.project_id (1:1)
// 
// ============================================================`;
};

// Generate Excel-specific Power Query script (separate queries per table)
const generateExcelScript = (baseUrl, apiKey) => {
  return `// ============================================================
// Pulse Data Connector for Microsoft Excel Data Model
// Generated: ${new Date().toISOString()}
// ============================================================
//
// INSTRUCTIONS FOR EXCEL:
// 1. Open Excel and go to Data tab > Get Data > From Other Sources > Blank Query
// 2. Click "Advanced Editor" in the Power Query Editor toolbar
//
// STEP 1 - CREATE THE PARAMETERS (do this first, only once):
//   a. Paste the "BaseUrl" parameter query below into a new blank query
//   b. Name it exactly "BaseUrl" and click Done
//   c. Repeat for "ApiKey"
//
// STEP 2 - CREATE TABLE QUERIES:
//   a. For each table section below, create a new blank query
//   b. Paste the query code into Advanced Editor
//   c. Name it (e.g., "Users", "Projects") and click Done
//   d. Click "Close & Load To..." > "Only Create Connection" 
//      and check "Add this data to the Data Model"
//
// STEP 3 - BUILD RELATIONSHIPS:
//   After loading all tables, go to Power Pivot > Diagram View
//   to create relationships (see guide at the bottom)
//
// To change the connection, just update the BaseUrl and ApiKey
// parameter queries - all table queries will update automatically.
//
// ============================================================


// ==================== PARAMETER: BaseUrl ====================
// Create a new blank query, paste this, and name it exactly "BaseUrl"

"${baseUrl}" meta [IsParameterQuery=true, Type="Text", IsParameterQueryRequired=true]


// ==================== PARAMETER: ApiKey ====================
// Create a new blank query, paste this, and name it exactly "ApiKey"

"${apiKey || 'YOUR_API_KEY_HERE'}" meta [IsParameterQuery=true, Type="Text", IsParameterQueryRequired=true]


// ==================== TABLE: Users ====================
// Application users (Account Owners, Admins)
// Create a new blank query, paste this, and name it "Users"

let
    Url = BaseUrl & "/api/admin/powerbi/users?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "email", "first_name", "last_name", "organization_id", "role", "is_global_admin", "created_at"})
in
    Expanded


// ==================== TABLE: Organizations ====================
// Customer organizations/accounts
// Create a new blank query, paste this, and name it "Organizations"

let
    Url = BaseUrl & "/api/admin/powerbi/organizations?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "name", "owner_id", "owner_email", "owner_name", "trial_ends_at", "created_at"})
in
    Expanded


// ==================== TABLE: Projects ====================
// Projects within organizations
// Create a new blank query, paste this, and name it "Projects"

let
    Url = BaseUrl & "/api/admin/powerbi/projects?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "name", "description", "organization_id", "organization_name", "created_by", "project_manager", "is_active", "created_at"})
in
    Expanded


// ==================== TABLE: Stakeholders ====================
// Project stakeholders (invited users)
// Create a new blank query, paste this, and name it "Stakeholders"

let
    Url = BaseUrl & "/api/admin/powerbi/stakeholders?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "first_name", "last_name", "name", "email", "project_id", "project_name", "organization_id", "organization_name", "status", "source", "is_reference_only", "segment_count", "created_at"})
in
    Expanded


// ==================== TABLE: Stakeholder_Segments ====================
// Junction table: Links stakeholders to their segment assignments
// Create a new blank query, paste this, and name it "Stakeholder_Segments"

let
    Url = BaseUrl & "/api/admin/powerbi/stakeholders?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "segment_assignments"}),
    FilteredRows = Table.SelectRows(Expanded, each [segment_assignments] <> null and List.Count([segment_assignments]) > 0),
    ExpandedList = Table.ExpandListColumn(FilteredRows, "segment_assignments"),
    ExpandedRecords = Table.ExpandRecordColumn(ExpandedList, "segment_assignments", {"segment_id", "segment_name", "segment_type"}),
    RenamedColumns = Table.RenameColumns(ExpandedRecords, {{"id", "stakeholder_id"}})
in
    RenamedColumns


// ==================== TABLE: SegmentTypes ====================
// Categories of segments (e.g., Department, Location)
// Create a new blank query, paste this, and name it "SegmentTypes"

let
    Url = BaseUrl & "/api/admin/powerbi/segment-types?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "name", "description", "icon", "project_id", "project_name", "segment_count", "created_at"})
in
    Expanded


// ==================== TABLE: Segments ====================
// Individual segments within types
// Create a new blank query, paste this, and name it "Segments"

let
    Url = BaseUrl & "/api/admin/powerbi/segments?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "name", "description", "segment_type_id", "segment_type_name", "project_id", "project_name", "stakeholder_count", "created_at"})
in
    Expanded


// ==================== TABLE: Drivers ====================
// Sentiment drivers (categories for questions)
// Create a new blank query, paste this, and name it "Drivers"

let
    Url = BaseUrl & "/api/admin/powerbi/drivers?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "name", "description", "project_id", "project_name", "order", "question_count", "created_at"})
in
    Expanded


// ==================== TABLE: Questions ====================
// Survey questions (About Me, About Others, Public)
// Create a new blank query, paste this, and name it "Questions"

let
    Url = BaseUrl & "/api/admin/powerbi/questions?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "text", "heading", "question_type", "question_context", "driver_id", "driver_name", "project_id", "project_name", "order", "created_at"})
in
    Expanded


// ==================== TABLE: SurveyResponses ====================
// About Me survey responses
// Create a new blank query, paste this, and name it "SurveyResponses"

let
    Url = BaseUrl & "/api/admin/powerbi/survey-responses?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "project_id", "project_name", "question_id", "question_heading", "question_type", "stakeholder_id", "stakeholder_name", "stakeholder_email", "answer", "skipped", "created_at"})
in
    Expanded


// ==================== TABLE: AboutOthersResponses ====================
// About Others survey responses
// Create a new blank query, paste this, and name it "AboutOthersResponses"

let
    Url = BaseUrl & "/api/admin/powerbi/about-others-responses?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "project_id", "project_name", "selection_id", "question_id", "question_heading", "question_type", "respondent_id", "respondent_email", "respondent_name", "target_id", "target_name", "target_email", "answer", "created_at"})
in
    Expanded


// ==================== TABLE: AboutOthersSelections ====================
// Who selected whom for About Others
// Create a new blank query, paste this, and name it "AboutOthersSelections"

let
    Url = BaseUrl & "/api/admin/powerbi/about-others-selections?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "project_id", "project_name", "user_id", "user_email", "user_name", "target_type", "target_id", "target_name", "created_at"})
in
    Expanded


// ==================== TABLE: Invitations ====================
// Stakeholder invitations
// Create a new blank query, paste this, and name it "Invitations"

let
    Url = BaseUrl & "/api/admin/powerbi/invitations?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "project_id", "project_name", "stakeholder_id", "stakeholder_name", "stakeholder_email", "token", "status", "expires_at", "created_at"})
in
    Expanded


// ==================== TABLE: Subscriptions ====================
// Account owner subscriptions
// Create a new blank query, paste this, and name it "Subscriptions"

let
    Url = BaseUrl & "/api/admin/powerbi/subscriptions?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "account_owner_id", "account_owner_email", "account_owner_name", "organization_id", "organization_name", "tier_id", "tier_name", "status", "paid_users", "trial_start", "trial_end", "max_users_override", "max_projects_override", "created_at"})
in
    Expanded


// ==================== TABLE: PricingTiers ====================
// Pricing plan definitions
// Create a new blank query, paste this, and name it "PricingTiers"

let
    Url = BaseUrl & "/api/admin/powerbi/pricing-tiers?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "name", "display_name", "price_per_user", "min_users", "max_users", "min_monthly_cost", "max_projects", "trial_days", "is_active", "subscription_count", "created_at"})
in
    Expanded


// ==================== TABLE: PricingTier_Features ====================
// Junction table: Pricing tier features expanded
// Create a new blank query, paste this, and name it "PricingTier_Features"

let
    Url = BaseUrl & "/api/admin/powerbi/pricing-tiers?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "display_name", "features"}),
    FilteredRows = Table.SelectRows(Expanded, each [features] <> null and List.Count([features]) > 0),
    ExpandedList = Table.ExpandListColumn(FilteredRows, "features"),
    RenamedColumns = Table.RenameColumns(ExpandedList, {{"id", "tier_id"}, {"features", "feature"}})
in
    RenamedColumns


// ==================== TABLE: DashboardConfigs ====================
// Dashboard configuration per project
// Create a new blank query, paste this, and name it "DashboardConfigs"

let
    Url = BaseUrl & "/api/admin/powerbi/dashboard-configs?api_key=" & ApiKey,
    Source = Json.Document(Web.Contents(Url)),
    ToTable = Table.FromList(Source, Splitter.SplitByNothing(), null, null, ExtraValues.Error),
    Expanded = Table.ExpandRecordColumn(ToTable, "Column1", {"id", "project_id", "project_name", "top_line_question_id", "top_line_question", "anonymity_threshold", "updated_at"})
in
    Expanded


// ============================================================
// RELATIONSHIP GUIDE FOR EXCEL DATA MODEL:
// ============================================================
// After loading tables, go to Power Pivot → Diagram View to create relationships:
//
// PRIMARY RELATIONSHIPS:
// - Users[id] → Organizations[owner_id]
// - Organizations[id] → Projects[organization_id]
// - Projects[id] → Stakeholders[project_id]
// - Stakeholders[id] → Stakeholder_Segments[stakeholder_id]
// - Segments[id] → Stakeholder_Segments[segment_id]
//
// SECONDARY RELATIONSHIPS:
// - SegmentTypes[id] → Segments[segment_type_id]
// - Projects[id] → SegmentTypes[project_id]
// - Projects[id] → Drivers[project_id]
// - Drivers[id] → Questions[driver_id]
//
// RESPONSE RELATIONSHIPS:
// - Stakeholders[id] → SurveyResponses[stakeholder_id]
// - Questions[id] → SurveyResponses[question_id]
// - Questions[id] → AboutOthersResponses[question_id]
//
// SYSTEM RELATIONSHIPS:
// - Users[id] → Subscriptions[account_owner_id]
// - PricingTiers[id] → Subscriptions[tier_id]
// - PricingTiers[id] → PricingTier_Features[tier_id]
//
// ============================================================`;
};

// Power Query Script Dialog Component
const PowerQueryDialog = ({ baseUrl, apiKey }) => {
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState('powerbi'); // 'powerbi' or 'excel'
  const script = generatePowerQueryScript(baseUrl, apiKey, mode);
  const isExcel = mode === 'excel';
  
  const copyScript = () => {
    navigator.clipboard.writeText(script);
    setCopied(true);
    toast.success(`${isExcel ? 'Excel' : 'Power BI'} Power Query script copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const downloadScript = () => {
    const blob = new Blob([script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isExcel ? 'pulse_excel_power_query.m' : 'pulse_powerbi_power_query.m';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${isExcel ? 'Excel' : 'Power BI'} Power Query script downloaded`);
  };
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="generate-power-query-btn">
          <FileCode className="w-4 h-4" />
          Generate Power Query Script
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode className="w-5 h-5 text-[#5AA2AE]" />
            Power Query M Script
          </DialogTitle>
          <DialogDescription>
            Generate a script for {isExcel ? 'Excel Data Model' : 'Power BI Desktop'} to load all tables with proper relationships.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Mode Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <Label htmlFor="mode-toggle" className="font-medium">Target Application:</Label>
              <div className="flex items-center gap-2 text-sm">
                <span className={mode === 'powerbi' ? 'font-semibold text-[#5AA2AE]' : 'text-slate-500'}>
                  Power BI
                </span>
                <Switch
                  id="mode-toggle"
                  checked={mode === 'excel'}
                  onCheckedChange={(checked) => setMode(checked ? 'excel' : 'powerbi')}
                />
                <span className={mode === 'excel' ? 'font-semibold text-green-600' : 'text-slate-500'}>
                  Excel
                </span>
              </div>
            </div>
            <Badge variant={isExcel ? 'default' : 'secondary'} className={isExcel ? 'bg-green-600' : ''}>
              {isExcel ? 'Excel Data Model' : 'Power BI Desktop'}
            </Badge>
          </div>
          
          {/* Instructions - Different for each mode */}
          <div className={`p-3 rounded-lg border text-sm ${isExcel ? 'bg-green-50 border-green-100' : 'bg-blue-50 border-blue-100'}`}>
            <p className={`font-medium mb-2 ${isExcel ? 'text-green-800' : 'text-blue-800'}`}>
              How to use in {isExcel ? 'Excel' : 'Power BI Desktop'}:
            </p>
            {isExcel ? (
              <ol className="text-green-700 space-y-1 list-decimal list-inside text-xs">
                <li>Open Excel and go to <strong>Data → Get Data → From Other Sources → Blank Query</strong></li>
                <li>Click <strong>Advanced Editor</strong> in the Power Query Editor toolbar</li>
                <li>Copy <strong>ONE table section at a time</strong> from the script below</li>
                <li>Paste and click <strong>Done</strong>, then name the query (e.g., "Users")</li>
                <li>Repeat for each table you need</li>
                <li>Click <strong>Close & Load To...</strong> → Select <strong>"Only Create Connection"</strong></li>
                <li>Check <strong>"Add this data to the Data Model"</strong></li>
                <li>Create relationships in <strong>Power Pivot → Diagram View</strong></li>
              </ol>
            ) : (
              <ol className="text-blue-700 space-y-1 list-decimal list-inside text-xs">
                <li>In Power BI Desktop, go to <strong>Home → Get Data → Blank Query</strong></li>
                <li>Click <strong>Advanced Editor</strong> in the toolbar</li>
                <li>Replace all content with this script and click <strong>Done</strong></li>
                <li>Right-click the query and select <strong>Convert To Table → Expand all columns</strong></li>
                <li>Each table will be loaded with arrays parsed into related junction tables</li>
              </ol>
            )}
          </div>
          
          {/* API Key Warning */}
          {!apiKey && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              <strong>Note:</strong> No API key detected. Generate a Power BI API key in the Power BI Integration section below, 
              then replace <code className="bg-amber-100 px-1 rounded">YOUR_API_KEY_HERE</code> in the script.
            </div>
          )}
          
          {/* Excel-specific note */}
          {isExcel && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-800">
              <strong>Excel Note:</strong> The Excel script contains <strong>separate queries for each table</strong>. 
              Copy one section at a time and create individual queries. This allows you to load only the tables you need.
            </div>
          )}
          
          {/* Script Preview */}
          <div className="relative">
            <Textarea 
              value={script} 
              readOnly 
              className="font-mono text-xs h-80 bg-slate-900 text-slate-100 resize-none"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <Button size="sm" variant="secondary" onClick={copyScript}>
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button size="sm" variant="secondary" onClick={downloadScript}>
                <Download className="w-4 h-4 mr-1" />
                Download .m
              </Button>
            </div>
          </div>
          
          {/* Tables Summary */}
          <div className="p-3 bg-slate-50 rounded-lg border text-xs">
            <p className="font-medium text-slate-700 mb-2">Tables included (17 total):</p>
            <div className="grid grid-cols-4 gap-2 text-slate-600">
              <div>
                <span className="font-medium text-blue-600">Primary:</span>
                <ul className="ml-2">
                  <li>Users</li>
                  <li>Organizations</li>
                  <li>Projects</li>
                  <li>Stakeholders</li>
                  <li className="text-green-600">+ Stakeholder_Segments</li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-teal-600">Secondary:</span>
                <ul className="ml-2">
                  <li>SegmentTypes</li>
                  <li>Segments</li>
                  <li>Drivers</li>
                  <li>Questions</li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-rose-600">Responses:</span>
                <ul className="ml-2">
                  <li>SurveyResponses</li>
                  <li>AboutOthersResponses</li>
                  <li>AboutOthersSelections</li>
                  <li>Invitations</li>
                </ul>
              </div>
              <div>
                <span className="font-medium text-slate-600">System:</span>
                <ul className="ml-2">
                  <li>Subscriptions</li>
                  <li>PricingTiers</li>
                  <li className="text-green-600">+ PricingTier_Features</li>
                  <li>DashboardConfigs</li>
                </ul>
              </div>
            </div>
            <p className="mt-2 text-green-600">
              <strong>+2 junction tables</strong> created from arrays: Stakeholder_Segments, PricingTier_Features
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Database schema definition with API endpoints
const DATABASE_SCHEMA = {
  // Primary/Core Tables
  primary: [
    {
      name: 'users',
      description: 'Application users (Account Owners, Admins)',
      color: 'bg-blue-500',
      apiEndpoint: '/api/admin/data-analysis/users',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'email', type: 'string', unique: true },
        { name: 'password_hash', type: 'string' },
        { name: 'first_name', type: 'string' },
        { name: 'last_name', type: 'string' },
        { name: 'organization_id', type: 'string', fk: 'organizations' },
        { name: 'role', type: 'enum', values: ['owner', 'admin', 'member'] },
        { name: 'is_global_admin', type: 'boolean' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'organizations',
      description: 'Customer organizations/accounts',
      color: 'bg-purple-500',
      apiEndpoint: '/api/admin/data-analysis/organizations',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'name', type: 'string' },
        { name: 'owner_id', type: 'string', fk: 'users' },
        { name: 'trial_ends_at', type: 'datetime' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'projects',
      description: 'Projects within organizations',
      color: 'bg-emerald-500',
      apiEndpoint: '/api/admin/data-analysis/projects',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'name', type: 'string' },
        { name: 'description', type: 'string' },
        { name: 'organization_id', type: 'string', fk: 'organizations' },
        { name: 'created_by', type: 'string', fk: 'users' },
        { name: 'project_manager', type: 'string' },
        { name: 'is_active', type: 'boolean' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'stakeholders',
      description: 'Project stakeholders (invited users)',
      color: 'bg-orange-500',
      apiEndpoint: '/api/admin/data-analysis/stakeholders',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'first_name', type: 'string' },
        { name: 'last_name', type: 'string' },
        { name: 'email', type: 'string' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'organization_id', type: 'string', fk: 'organizations' },
        { name: 'segment_assignments', type: 'array' },
        { name: 'status', type: 'enum', values: ['invited', 'reference', 'pending', 'rejected'] },
        { name: 'source', type: 'enum', values: ['admin_added', 'self_identified', 'anonymous'] },
        { name: 'created_at', type: 'datetime' }
      ]
    }
  ],
  // Secondary Tables
  secondary: [
    {
      name: 'segment_types',
      description: 'Categories of segments (e.g., Department, Location)',
      color: 'bg-teal-500',
      apiEndpoint: '/api/admin/data-analysis/segment-types',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'name', type: 'string' },
        { name: 'description', type: 'string' },
        { name: 'icon', type: 'string' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'segments',
      description: 'Individual segments within types',
      color: 'bg-cyan-500',
      apiEndpoint: '/api/admin/data-analysis/segments',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'name', type: 'string' },
        { name: 'description', type: 'string' },
        { name: 'segment_type_id', type: 'string', fk: 'segment_types' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'visible_segment_ids', type: 'array' },
        { name: 'visible_dashboard_pages', type: 'array' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'drivers',
      description: 'Sentiment drivers (categories for questions)',
      color: 'bg-indigo-500',
      apiEndpoint: '/api/admin/data-analysis/drivers',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'name', type: 'string' },
        { name: 'description', type: 'string' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'order', type: 'number' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'questions',
      description: 'Survey questions (About Me, About Others, Public)',
      color: 'bg-pink-500',
      apiEndpoint: '/api/admin/data-analysis/questions',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'text', type: 'string' },
        { name: 'heading', type: 'string' },
        { name: 'question_type', type: 'enum', values: ['slider', 'free_text', 'multiple_choice', 'checkbox'] },
        { name: 'question_context', type: 'enum', values: ['about_me', 'about_others', 'public_landing'] },
        { name: 'driver_id', type: 'string', fk: 'drivers' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'options', type: 'array' },
        { name: 'slider_config', type: 'object' },
        { name: 'order', type: 'number' },
        { name: 'created_at', type: 'datetime' }
      ]
    }
  ],
  // Response & Transaction Tables
  transactional: [
    {
      name: 'survey_responses',
      description: 'About Me survey responses',
      color: 'bg-rose-500',
      apiEndpoint: '/api/admin/data-analysis/survey-responses',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'question_id', type: 'string', fk: 'questions' },
        { name: 'stakeholder_id', type: 'string', fk: 'stakeholders' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'answer', type: 'any' },
        { name: 'skipped', type: 'boolean' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'about_others_responses',
      description: 'About Others survey responses',
      color: 'bg-amber-500',
      apiEndpoint: '/api/admin/data-analysis/about-others-responses',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'question_id', type: 'string', fk: 'questions' },
        { name: 'selection_id', type: 'string', fk: 'about_others_selections' },
        { name: 'user_id', type: 'string', fk: 'users' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'answer', type: 'any' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'about_others_selections',
      description: 'User selections for About Others',
      color: 'bg-lime-500',
      apiEndpoint: '/api/admin/data-analysis/about-others-selections',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'user_id', type: 'string', fk: 'users' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'target_type', type: 'enum', values: ['stakeholder', 'segment'] },
        { name: 'target_id', type: 'string' },
        { name: 'mapping_category_ids', type: 'array' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'invitations',
      description: 'Stakeholder invitations',
      color: 'bg-violet-500',
      apiEndpoint: '/api/admin/data-analysis/invitations',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'stakeholder_id', type: 'string', fk: 'stakeholders' },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'token', type: 'string' },
        { name: 'status', type: 'enum', values: ['pending', 'accepted', 'expired'] },
        { name: 'expires_at', type: 'datetime' },
        { name: 'created_at', type: 'datetime' }
      ]
    }
  ],
  // System/Config Tables
  system: [
    {
      name: 'subscriptions',
      description: 'Account owner subscriptions',
      color: 'bg-sky-500',
      apiEndpoint: '/api/admin/data-analysis/subscriptions',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'account_owner_id', type: 'string', fk: 'users' },
        { name: 'organization_id', type: 'string', fk: 'organizations' },
        { name: 'tier_id', type: 'string', fk: 'pricing_tiers' },
        { name: 'status', type: 'enum', values: ['trial', 'active', 'suspended'] },
        { name: 'paid_users', type: 'number' },
        { name: 'trial_end', type: 'datetime' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'pricing_tiers',
      description: 'Pricing plan definitions',
      color: 'bg-fuchsia-500',
      apiEndpoint: '/api/admin/data-analysis/pricing-tiers',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'name', type: 'string' },
        { name: 'display_name', type: 'string' },
        { name: 'price_per_user', type: 'number' },
        { name: 'min_users', type: 'number' },
        { name: 'max_users', type: 'number' },
        { name: 'features', type: 'array' },
        { name: 'is_active', type: 'boolean' }
      ]
    },
    {
      name: 'api_keys',
      description: 'Power BI API keys',
      color: 'bg-slate-500',
      apiEndpoint: '/api/admin/data-analysis/api-keys',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'user_id', type: 'string', fk: 'users' },
        { name: 'key', type: 'string' },
        { name: 'created_at', type: 'datetime' }
      ]
    },
    {
      name: 'dashboard_configs',
      description: 'Dashboard configuration per project',
      color: 'bg-stone-500',
      apiEndpoint: '/api/admin/data-analysis/dashboard-configs',
      fields: [
        { name: 'id', type: 'string', primary: true },
        { name: 'project_id', type: 'string', fk: 'projects' },
        { name: 'top_line_question_id', type: 'string', fk: 'questions' },
        { name: 'response_summary_question_ids', type: 'array' },
        { name: 'anonymity_threshold', type: 'number' },
        { name: 'updated_at', type: 'datetime' }
      ]
    }
  ]
};

// Relationships between tables
const RELATIONSHIPS = [
  { from: 'users', to: 'organizations', label: 'belongs to', type: '1:1' },
  { from: 'organizations', to: 'users', label: 'owned by', type: '1:1' },
  { from: 'projects', to: 'organizations', label: 'belongs to', type: 'N:1' },
  { from: 'projects', to: 'users', label: 'created by', type: 'N:1' },
  { from: 'stakeholders', to: 'projects', label: 'belongs to', type: 'N:1' },
  { from: 'segment_types', to: 'projects', label: 'belongs to', type: 'N:1' },
  { from: 'segments', to: 'segment_types', label: 'belongs to', type: 'N:1' },
  { from: 'drivers', to: 'projects', label: 'belongs to', type: 'N:1' },
  { from: 'questions', to: 'projects', label: 'belongs to', type: 'N:1' },
  { from: 'questions', to: 'drivers', label: 'categorized by', type: 'N:1' },
  { from: 'survey_responses', to: 'questions', label: 'answers', type: 'N:1' },
  { from: 'survey_responses', to: 'stakeholders', label: 'from', type: 'N:1' },
  { from: 'about_others_responses', to: 'questions', label: 'answers', type: 'N:1' },
  { from: 'about_others_selections', to: 'users', label: 'by', type: 'N:1' },
  { from: 'invitations', to: 'stakeholders', label: 'for', type: '1:1' },
  { from: 'subscriptions', to: 'users', label: 'for', type: '1:1' },
  { from: 'subscriptions', to: 'pricing_tiers', label: 'uses', type: 'N:1' }
];

// Table Card Component
const TableCard = ({ table, expanded, onToggle, showDetails, onTestApi, isLoading }) => {
  const fieldCount = table.fields.length;
  const pkField = table.fields.find(f => f.primary);
  const fkFields = table.fields.filter(f => f.fk);
  const [copied, setCopied] = useState(false);
  
  const copyEndpoint = (e) => {
    e.stopPropagation();
    if (table.apiEndpoint) {
      navigator.clipboard.writeText(table.apiEndpoint);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  return (
    <div 
      className={`border rounded-lg overflow-hidden transition-all duration-200 ${
        expanded ? 'shadow-lg ring-2 ring-offset-2 ring-slate-300' : 'hover:shadow-md'
      }`}
      data-testid={`erd-table-${table.name}`}
    >
      {/* Header */}
      <div 
        className={`${table.color} text-white px-3 py-2 cursor-pointer flex items-center justify-between`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <Table2 className="w-4 h-4" />
          <span className="font-semibold text-sm">{table.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-white/20 text-white text-xs">
            {fieldCount} fields
          </Badge>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </div>
      
      {/* Description */}
      <div className="px-3 py-2 bg-slate-50 border-b text-xs text-slate-600">
        {table.description}
      </div>
      
      {/* API Endpoint */}
      {table.apiEndpoint && (
        <div className="px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-50 border-b flex items-center gap-2">
          <code className="text-xs font-mono text-slate-600 flex-1 truncate">
            GET {table.apiEndpoint}
          </code>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={copyEndpoint}
                >
                  {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{copied ? 'Copied!' : 'Copy endpoint'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTestApi(table);
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 text-green-600" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Test API</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      
      {/* Fields (expandable) */}
      {expanded && showDetails && (
        <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
          {table.fields.map(field => (
            <div 
              key={field.name} 
              className={`px-3 py-1.5 text-xs flex items-center gap-2 ${
                field.primary ? 'bg-amber-50' : field.fk ? 'bg-blue-50' : ''
              }`}
            >
              {field.primary && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Key className="w-3 h-3 text-amber-500" />
                    </TooltipTrigger>
                    <TooltipContent>Primary Key</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {field.fk && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Link2 className="w-3 h-3 text-blue-500" />
                    </TooltipTrigger>
                    <TooltipContent>Foreign Key → {field.fk}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <span className={`font-medium ${field.primary ? 'text-amber-700' : field.fk ? 'text-blue-700' : 'text-slate-700'}`}>
                {field.name}
              </span>
              <span className="text-slate-400 ml-auto">
                {field.type}
                {field.values && ` (${field.values.join(', ')})`}
              </span>
            </div>
          ))}
        </div>
      )}
      
      {/* Summary when collapsed */}
      {(!expanded || !showDetails) && (
        <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-3">
          {pkField && (
            <span className="flex items-center gap-1">
              <Key className="w-3 h-3 text-amber-500" />
              {pkField.name}
            </span>
          )}
          {fkFields.length > 0 && (
            <span className="flex items-center gap-1">
              <Link2 className="w-3 h-3 text-blue-500" />
              {fkFields.length} FK{fkFields.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Main ERD Component
export default function DatabaseERD() {
  const [expandedTables, setExpandedTables] = useState(new Set(['users', 'projects', 'stakeholders']));
  const [showDetails, setShowDetails] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [apiResult, setApiResult] = useState(null);
  const [loadingTable, setLoadingTable] = useState(null);
  const [schemaStats, setSchemaStats] = useState(null);
  const [powerbiKey, setPowerbiKey] = useState(null);
  
  // Get the base URL for Power BI script
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Fetch schema stats and Power BI key on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, keyRes] = await Promise.all([
          api.get('/admin/data-analysis/schema-stats'),
          api.get('/admin/powerbi/get-key').catch(() => ({ data: {} }))
        ]);
        setSchemaStats(statsRes.data);
        if (keyRes.data?.api_key) {
          setPowerbiKey(keyRes.data.api_key);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, []);
  
  const toggleTable = (tableName) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };
  
  const testApi = async (table) => {
    if (!table.apiEndpoint) return;
    
    setLoadingTable(table.name);
    try {
      const endpoint = table.apiEndpoint.replace('/api', '');
      const res = await api.get(endpoint);
      const data = res.data;
      setApiResult({
        table: table.name,
        endpoint: table.apiEndpoint,
        count: Array.isArray(data) ? data.length : 1,
        sample: Array.isArray(data) ? data.slice(0, 3) : data,
        success: true
      });
      toast.success(`${table.name}: ${Array.isArray(data) ? data.length : 1} records found`);
    } catch (err) {
      setApiResult({
        table: table.name,
        endpoint: table.apiEndpoint,
        error: err.response?.data?.detail || err.message,
        success: false
      });
      toast.error(`Failed to fetch ${table.name}`);
    } finally {
      setLoadingTable(null);
    }
  };
  
  const expandAll = () => {
    const allTables = [
      ...DATABASE_SCHEMA.primary,
      ...DATABASE_SCHEMA.secondary,
      ...DATABASE_SCHEMA.transactional,
      ...DATABASE_SCHEMA.system
    ].map(t => t.name);
    setExpandedTables(new Set(allTables));
  };
  
  const collapseAll = () => {
    setExpandedTables(new Set());
  };
  
  const getFilteredSchema = () => {
    if (selectedCategory === 'all') return DATABASE_SCHEMA;
    return {
      primary: selectedCategory === 'primary' ? DATABASE_SCHEMA.primary : [],
      secondary: selectedCategory === 'secondary' ? DATABASE_SCHEMA.secondary : [],
      transactional: selectedCategory === 'transactional' ? DATABASE_SCHEMA.transactional : [],
      system: selectedCategory === 'system' ? DATABASE_SCHEMA.system : []
    };
  };
  
  const getDocCount = (tableName) => {
    if (!schemaStats?.collections) return null;
    const col = schemaStats.collections.find(c => c.name === tableName);
    return col?.document_count;
  };
  
  const schema = getFilteredSchema();
  const totalTables = DATABASE_SCHEMA.primary.length + DATABASE_SCHEMA.secondary.length + 
                      DATABASE_SCHEMA.transactional.length + DATABASE_SCHEMA.system.length;
  const totalFields = [...DATABASE_SCHEMA.primary, ...DATABASE_SCHEMA.secondary, 
                       ...DATABASE_SCHEMA.transactional, ...DATABASE_SCHEMA.system]
                       .reduce((sum, t) => sum + t.fields.length, 0);
  
  return (
    <Card data-testid="database-erd-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-[#5AA2AE]" />
              Database Schema (ERD)
            </CardTitle>
            <CardDescription>
              Entity Relationship Diagram showing {totalTables} collections and {totalFields} fields
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PowerQueryDialog baseUrl={baseUrl} apiKey={powerbiKey} />
            <Button variant="outline" size="sm" onClick={collapseAll}>
              <Minimize2 className="w-4 h-4 mr-1" />
              Collapse
            </Button>
            <Button variant="outline" size="sm" onClick={expandAll}>
              <Maximize2 className="w-4 h-4 mr-1" />
              Expand All
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Category Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500">Filter:</span>
          {[
            { id: 'all', label: 'All Tables', count: totalTables },
            { id: 'primary', label: 'Primary', count: DATABASE_SCHEMA.primary.length, color: 'bg-blue-500' },
            { id: 'secondary', label: 'Secondary', count: DATABASE_SCHEMA.secondary.length, color: 'bg-teal-500' },
            { id: 'transactional', label: 'Responses', count: DATABASE_SCHEMA.transactional.length, color: 'bg-rose-500' },
            { id: 'system', label: 'System', count: DATABASE_SCHEMA.system.length, color: 'bg-slate-500' }
          ].map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={selectedCategory === cat.id ? '' : ''}
              data-testid={`erd-filter-${cat.id}`}
            >
              {cat.color && <span className={`w-2 h-2 rounded-full ${cat.color} mr-1.5`} />}
              {cat.label}
              <Badge variant="secondary" className="ml-1.5 text-xs">{cat.count}</Badge>
            </Button>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg text-xs">
          <span className="text-slate-500 font-medium">Legend:</span>
          <span className="flex items-center gap-1">
            <Key className="w-3 h-3 text-amber-500" />
            Primary Key
          </span>
          <span className="flex items-center gap-1">
            <Link2 className="w-3 h-3 text-blue-500" />
            Foreign Key
          </span>
          <span className="flex items-center gap-1">
            <Star className="w-3 h-3 text-emerald-500" />
            Primary Table
          </span>
        </div>
        
        {/* Primary Tables */}
        {schema.primary.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-emerald-500" />
              <h3 className="font-semibold text-slate-700">Primary Tables</h3>
              <span className="text-xs text-slate-400">Core business entities</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {schema.primary.map(table => (
                <TableCard
                  key={table.name}
                  table={table}
                  expanded={expandedTables.has(table.name)}
                  onToggle={() => toggleTable(table.name)}
                  showDetails={showDetails}
                  onTestApi={testApi}
                  isLoading={loadingTable === table.name}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Secondary Tables */}
        {schema.secondary.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Table2 className="w-4 h-4 text-teal-500" />
              <h3 className="font-semibold text-slate-700">Secondary Tables</h3>
              <span className="text-xs text-slate-400">Supporting structures</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {schema.secondary.map(table => (
                <TableCard
                  key={table.name}
                  table={table}
                  expanded={expandedTables.has(table.name)}
                  onToggle={() => toggleTable(table.name)}
                  showDetails={showDetails}
                  onTestApi={testApi}
                  isLoading={loadingTable === table.name}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Transactional Tables */}
        {schema.transactional.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Table2 className="w-4 h-4 text-rose-500" />
              <h3 className="font-semibold text-slate-700">Response & Transaction Tables</h3>
              <span className="text-xs text-slate-400">Survey responses and user actions</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {schema.transactional.map(table => (
                <TableCard
                  key={table.name}
                  table={table}
                  expanded={expandedTables.has(table.name)}
                  onToggle={() => toggleTable(table.name)}
                  showDetails={showDetails}
                  onTestApi={testApi}
                  isLoading={loadingTable === table.name}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* System Tables */}
        {schema.system.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Table2 className="w-4 h-4 text-slate-500" />
              <h3 className="font-semibold text-slate-700">System & Configuration Tables</h3>
              <span className="text-xs text-slate-400">Billing, settings, and API keys</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {schema.system.map(table => (
                <TableCard
                  key={table.name}
                  table={table}
                  expanded={expandedTables.has(table.name)}
                  onToggle={() => toggleTable(table.name)}
                  showDetails={showDetails}
                  onTestApi={testApi}
                  isLoading={loadingTable === table.name}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* API Test Result */}
        {apiResult && (
          <div className={`p-4 rounded-lg border ${apiResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className={`font-semibold ${apiResult.success ? 'text-green-700' : 'text-red-700'}`}>
                API Test: {apiResult.table}
              </h4>
              <Button variant="ghost" size="sm" onClick={() => setApiResult(null)}>
                Close
              </Button>
            </div>
            <code className="text-xs font-mono block mb-2 text-slate-600">
              GET {apiResult.endpoint}
            </code>
            {apiResult.success ? (
              <>
                <p className="text-sm text-green-600 mb-2">
                  ✓ Success: {apiResult.count} records found
                </p>
                {apiResult.sample && apiResult.sample.length > 0 && (
                  <div className="bg-white rounded p-2 border max-h-48 overflow-auto">
                    <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                      {JSON.stringify(apiResult.sample, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-red-600">
                ✗ Error: {apiResult.error}
              </p>
            )}
          </div>
        )}
        
        {/* Relationships Summary */}
        <div className="mt-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border">
          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Key Relationships
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
            {RELATIONSHIPS.slice(0, 12).map((rel, i) => (
              <div key={i} className="flex items-center gap-1 text-slate-600">
                <span className="font-medium text-slate-700">{rel.from}</span>
                <span className="text-slate-400">→</span>
                <span className="font-medium text-slate-700">{rel.to}</span>
                <span className="text-slate-400 ml-1">({rel.type})</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Info Note */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>MongoDB Collections:</strong> This application uses MongoDB as its database. 
            The schema above shows the document structure for each collection. Foreign keys are 
            implemented as string references (not enforced by the database).
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
